import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import { api } from "../../../../../../../../utility/AxiosInceptor";
import { Dialog } from "primereact/dialog";
import AddAgentDetail from "./Dialogs/add_agent_detail";
import { ProgressBar } from "primereact/progressbar";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { FileIcon, UploadFile } from "../../../../../../../../utility";
import FileUpdateDetails from "../../FileUploadDetails/FileUpdateDetailsBulk";
import FileStatsBulkUpdate from "../../FileStats/FileStatsBulkUpdate";
import { useNavigate } from "react-router-dom";
/** ---------------------------------------------------------
 *  BULK INVENTORY UPDATE COMPONENT
 *  Cleaned + Structured + Commented version
 *  ---------------------------------------------------------
 */
export default function BulkUpdate({
  setRefreshInventoryData,
  setUpdateInventory,
  setAddUnits,
  isFromhome,
}) {
  /** -----------------------
   *  Refs & Local States
   * ------------------------*/
  const ref = useRef(null);
  const [filename, setFilename] = useState(null);
  const navigate = useNavigate();
  const [fileUploadDetails, setFileUploadDetails] = useState(false);
  const [fileUploadStatus, setFileUploadStatus] = useState("");
  const [showStats, setShowStats] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [addAgentDialogVisibility, setAddAgentDialogVisibility] =
    useState(false);

  /** User Login Data */
  const loginRes = JSON.parse(localStorage.getItem("userData"));

  /** Stats & File Data */
  const [data, setData] = useState([]);
  const [header, setHeader] = useState("");
  const [duplicateRows, setDuplicateRows] = useState([]);
  const [color, setColor] = useState("success");

  /** -----------------------
   *  Formik (Form Handler)
   * ------------------------*/
  const formik = useFormik({
    initialValues: {
      file: "",
      uploaded_by: loginRes?._id,
    },

    onSubmit: (values, { resetForm }) => {
      if (!formik.values.file) return;

      setIsLoading(true);

      /** Prepare FormData */
      const formData = new FormData();
      formData.append("file", formik.values.file);
      formData.append("uploaded_by", formik.values.uploaded_by);

      /** API CALL */
      api
        .put(`/api/web/simInventory/bulkUpdateInventory`, formData)
        .then((res) => {
          const uploadedInventoryResult = res?.data?.data;
          setFileUploadStatus(uploadedInventoryResult);
          setIsLoading(false);

          /** Check if any error arrays have length */
          const hasErrors = [
            uploadedInventoryResult?.existingSimNumbers,
            uploadedInventoryResult?.existingIMEINumbers,
            uploadedInventoryResult?.invalidSIMs,
            uploadedInventoryResult?.noBoxNoAddedForSIMS,
            uploadedInventoryResult?.emptySIMRows,
            uploadedInventoryResult?.invalidIMEILength,
            uploadedInventoryResult?.duplicateRows,
          ].some((arr) => (arr?.length ?? 0) > 0);

          /** Show Toast */
          ref.current.show({
            severity: hasErrors ? "error" : "success",
            summary: "Inventory",
            detail: <ApiResponseShow res={res} />,
          });

          resetForm();
          setFilename(null);
          setProgress(0);
          setFileUploadDetails(true);
        })
        .catch((e) => {
          setIsLoading(false);
          ref.current.show({
            severity: "error",
            summary: "Inventory",
            detail: "Bulk Update Failed",
          });
        });
    },
  });

  /** ---------------------------------------------------------
   *  RENDER API RESPONSE (SHOW ERRORS / SUCCESS DETAILS)
   * --------------------------------------------------------- */
  function ApiResponseShow({ res }) {
    const data = res?.data?.data;

    return (
      <div
        style={{ overflowY: "auto", height: "85vh" }}
        className="mainerror flex flex-wrap justify-content-left"
      >
        {/* Updated Records */}
        <ResponseBlock
          title="Updated Records"
          list={data?.updated?.map((item) => JSON.stringify(item))} // stringify if object
          full
        />

        {/* Mapped Records */}
        <ResponseBlock
          title="Mapped Records"
          list={data?.mapping?.map((item) => JSON.stringify(item))}
          full
        />

        {/* Not Found Records */}
        <ResponseBlock
          title="Not Found Records"
          list={data?.notFound?.map(
            (item) =>
              `Lookup_ESN: ${item.row.Lookup_ESN}, New_ESN: ${item.row.New_ESN}, Box: ${item.row.Box}, IMEI: ${item.row.IMEI} - Reason: ${item.reason}`,
          )}
          full
        />

        {/* Validation Errors */}
        <ResponseBlock
          title="Validation Errors"
          list={data?.validationErrors?.map(
            (item) => `Row: ${item.row} - Reason: ${item.reason}`,
          )}
          full
        />
      </div>
    );
  }

  /** ---------------------------------------------------------
   *  SMALL BLOCK COMPONENT FOR REPEATED LIST SECTIONS
   * --------------------------------------------------------- */
  function ResponseBlock({ title, list, full = false }) {
    return (
      <div className={`mt-3 ${full ? "w-full" : ""}`}>
        <p>
          {title}: {list?.length ?? 0}
        </p>
        <ul className="m-0 list-none errormsg">
          {list?.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>
    );
  }

  /** ---------------------------------------------------------
   *  MAIN RETURN UI
   * --------------------------------------------------------- */
  return (
    <>
      {/* If file upload is done → show results */}
      {fileUploadDetails ? (
        <FileUpdateDetails
          fileUploadStatus={fileUploadStatus}
          setShowStats={setShowStats}
          setData={setData}
          isFromBulkUpdate={true}
          setFileUploadDetails={setFileUploadDetails}
          setAddUnits={setAddUnits}
          setHeader={setHeader}
          setDuplicateRows={setDuplicateRows}
          setColor={setColor}
        />
      ) : /* Showing Stats Screen */
      showStats ? (
        <FileStatsBulkUpdate
          data={data}
          header={header}
          setShowStats={setShowStats}
          setFileUploadDetails={setFileUploadDetails}
          duplicateRows={duplicateRows}
          color={color}
        />
      ) : (
        /* Default Upload Screen */
        <div
          className="addunitmain"
          style={{
            height: formik.values.upload === "Bulk" ? "83.1rem" : "72.9rem",
          }}
        >
          <div className="addunitinner">
            {/* Back Button */}
            <button
              className="backicon"
              onClick={() => {
                if (!isFromhome) {
                  setRefreshInventoryData((prev) => !prev);
                  setUpdateInventory(false);
                } else {
                  navigate("/manageinventory");
                }
              }}
            >
              <i
                className="pi pi-angle-left"
                style={{ fontSize: "1.5rem", color: "#323232" }}
              ></i>
            </button>

            {/* Header */}
            <div className="header">
              <h1 className="heading">Update Inventory</h1>
              <p className="para">Upload File to update inventory.</p>
            </div>

            {/* Form */}
            <form onSubmit={formik.handleSubmit}>
              {/* -------------------- FILE UPLOAD SECTION -------------------- */}
              <div className="flex flex-column">
                <div className="fileuploadmain">
                  {/* SHOW PROGRESS BAR */}
                  {progress > 0 && progress < 100 ? (
                    <UploadProgress filename={filename} progress={progress} />
                  ) : progress === 0 ? (
                    <UploadFileSection
                      setFilename={setFilename}
                      setProgress={setProgress}
                      formik={formik}
                    />
                  ) : (
                    <UploadCompleted filename={filename} />
                  )}
                </div>
              </div>

              {/* -------------------- SAMPLE FILE -------------------- */}
              <SampleFileSection carrier={formik.values.carrier} />

              {/* -------------------- POPUP DIALOG -------------------- */}
              <Dialog
                style={{ width: "90vw" }}
                visible={addAgentDialogVisibility}
                onHide={() => setAddAgentDialogVisibility(false)}
              >
                <AddAgentDetail AgentName={"Dummy"} />
              </Dialog>

              {/* -------------------- ACTION BUTTONS -------------------- */}
              <div
                className="flex flex-wrap justify-content-end w-full"
                style={{ gap: "1.6rem", marginTop: "3.2rem" }}
              >
                <Button
                  className="backbtn"
                  label="Back"
                  type="button"
                  onClick={() => {
                    if (!isFromhome) {
                      setRefreshInventoryData((prev) => !prev);
                      setUpdateInventory(false);
                    } else {
                      navigate("/manageinventory");
                    }
                  }}
                />
                <Button
                  label="Submit"
                  className="nextbtn"
                  disabled={progress < 100 || isLoading}
                  type="submit"
                  loading={isLoading}
                />
              </div>

              {/* Toast */}
              <Toast ref={ref} />
            </form>
          </div>
        </div>
      )}
    </>
  );
}

/** ---------------------------------------------------------
 *  SUB-COMPONENTS (CLEANER UI BLOCKS)
 * --------------------------------------------------------- */

/** Upload Progress Component */
function UploadProgress({ filename, progress }) {
  return (
    <div className="progressbar">
      <div className="flex" style={{ gap: "1.2rem", height: "3.4rem" }}>
        <FileIcon />
        <div className="flex flex-column">
          <p>{filename}</p>
          <label>200 KB</label>
        </div>
      </div>

      <div className="flex" style={{ gap: "1.2rem" }}>
        <ProgressBar value={progress} showValue={false} />
        <p>{progress}%</p>
      </div>
    </div>
  );
}

/** Upload Completed */
function UploadCompleted({ filename }) {
  return (
    <div className="progressbar">
      <div className="flex" style={{ gap: "1.2rem", height: "3.4rem" }}>
        <FileIcon />
        <div className="flex flex-column">
          <p>{filename}</p>
          <label>200 KB</label>
        </div>
      </div>

      <div className="flex" style={{ gap: "1.2rem" }}>
        <p style={{ color: "#27AE60" }}>Completed</p>
      </div>
    </div>
  );
}

/** Upload File Section */
function UploadFileSection({ setFilename, setProgress, formik }) {
  return (
    <div className="fileuploadinner">
      <UploadFile />

      <h1>Drop file or browse</h1>
      <p>Format: .xlsx only & Max 25 MB</p>

      <Button
        type="button"
        label="Upload"
        className="backbtn"
        style={{ width: "30rem", height: "3.4rem", marginTop: "1.2rem" }}
        onClick={() => {
          let input = document.createElement("input");
          input.type = "file";
          input.accept =
            ".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
          input.click();

          input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return;

            setFilename(file.name);
            formik.setFieldValue("file", file);

            /** Fake upload animation */
            let p = 0;
            const interval = setInterval(() => {
              p += 10;
              setProgress(p);
              if (p >= 100) clearInterval(interval);
            }, 300);
          };
        }}
      />
    </div>
  );
}

/** Sample File Section */
function SampleFileSection({ carrier }) {
  return (
    <div className="samplefilemain">
      <p className="flex">
        <strong>
          <a href="/inventorybulkupdate/tmb.xlsx"> Sample File </a>
        </strong>
      </p>
    </div>
  );
}
